import { useState, useEffect, useRef, useCallback } from "react";
import { GestionHeader } from "./SiteHeader";
import { Html5Qrcode } from "html5-qrcode";
import { apiGet, apiSend, errorMessage } from "./api";
import "./ScanCard.css";

/** Le QR encode l'ID brut, mais un QR tiers peut contenir une URL le portant. */
function extractCardId(decodedText) {
  const match = decodedText.match(/CARD-[A-Za-z0-9]+/);
  return match ? match[0] : decodedText.trim();
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function ScanCard({ user, onNavigate, onLogout }) {
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");
  const [manualId, setManualId] = useState("");
  const [pointsInput, setPointsInput] = useState("");
  const [message, setMessage] = useState("");
  const [scanning, setScanning] = useState(false);
  const [cards, setCards] = useState([]);
  const [cardsTotal, setCardsTotal] = useState(0);
  const [cardsPage, setCardsPage] = useState(1);
  const [cardsPages, setCardsPages] = useState(1);
  const [cardSearch, setCardSearch] = useState("");
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);

  const stopScanner = () => {
    if (html5QrRef.current) {
      const instance = html5QrRef.current;
      html5QrRef.current = null;
      // clear() après stop() : sinon l'élément vidéo reste dans le DOM
      instance.stop().then(() => instance.clear()).catch(() => {});
    }
    setScanning(false);
  };

  const lookupCard = async (cardId) => {
    setError("");
    setMessage("");
    try {
      const data = await apiGet(`/api/cards/${encodeURIComponent(cardId)}`, "Carte introuvable");
      setCard(data);
    } catch (e) {
      setError(errorMessage(e, "Carte introuvable"));
      setCard(null);
    }
  };

  const startScanner = async () => {
    setError("");
    setScanning(true);
    try {
      const html5Qr = new Html5Qrcode("qr-reader");
      html5QrRef.current = html5Qr;
      await html5Qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          stopScanner();
          lookupCard(extractCardId(decodedText));
        },
        () => {}
      );
    } catch {
      setError("Impossible d'accéder à la caméra");
      setScanning(false);
    }
  };

  useEffect(() => {
    return () => stopScanner();
  }, []);

  // Même liste que l'écran Statistiques (paginée, filtrable) : ici elle sert
  // à retrouver un client sans scanner — un clic sur une ligne ouvre sa carte.
  // Silencieux en cas d'échec (plan sans accès, pas d'organisation) : le
  // scanner et la recherche par ID restent le cœur de la page.
  const fetchCards = useCallback(async () => {
    if (!user.organization_id) return;
    const params = new URLSearchParams({ page: cardsPage, limit: 20 });
    if (cardSearch.trim()) params.append("search", cardSearch.trim());
    try {
      const data = await apiGet(
        `/api/organizations/${user.organization_id}/cards?${params}`,
        "Impossible de charger les cartes");
      setCards(data.cards);
      setCardsTotal(data.total);
      setCardsPages(data.pages || 1);
    } catch {
      setCards([]);
      setCardsTotal(0);
    }
  }, [user.organization_id, cardsPage, cardSearch]);

  // Debounce : sans lui, une requête partirait à chaque frappe
  useEffect(() => {
    const timer = setTimeout(fetchCards, 300);
    return () => clearTimeout(timer);
  }, [fetchCards]);

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (manualId.trim()) {
      lookupCard(manualId.trim());
    }
  };

  const isStamps = card?.card_mode === "stamps";
  const stamps = card ? card.points % 10 : 0;
  const rewards = card ? Math.floor(card.points / 10) : 0;

  const handlePoints = async (amount, label) => {
    if (!card || !amount) return;
    setMessage("");
    setError("");
    try {
      const data = await apiSend(`/api/cards/${card.id}/points`, "POST", { points: amount },
        "Erreur lors de la mise à jour");
      const applied = data.points - card.points;
      setCard({ ...card, points: data.points });
      // Le serveur borne le solde à 0 : on annonce ce qui a réellement été appliqué
      if (applied !== amount) {
        setMessage(`Solde ramené à ${data.points} (retrait limité au solde disponible)`);
      } else {
        setMessage(label || (amount > 0 ? `+${amount} points ajoutés` : `${amount} points retirés`));
      }
      setPointsInput("");
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de la mise à jour"));
    }
  };

  return (
    <div className="scan-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Scanner Carte"
        onBack={() => { stopScanner(); onNavigate("home"); }}
        onHome={() => { stopScanner(); onNavigate("home"); }}
        onLogout={onLogout}
      />

      <main className="scan-main">
        {!card ? (
          <>
            <div id="qr-reader" ref={scannerRef} className="scan-reader" />
            {!scanning && (
              <button className="scan-btn" onClick={startScanner}>Activer la caméra</button>
            )}
            {scanning && (
              <button className="scan-btn scan-btn-stop" onClick={stopScanner}>Arrêter le scan</button>
            )}

            <div className="scan-divider">ou</div>

            <form className="scan-manual" onSubmit={handleManualSearch}>
              <input
                placeholder="Entrer l'ID de la carte (CARD-XXXX)"
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
              />
              <button type="submit">Rechercher</button>
            </form>

            {error && <div className="scan-error">{error}</div>}

            {(cardsTotal > 0 || cardSearch.trim()) && (
              <div className="scan-cards">
                <div className="scan-cards-head">
                  <h2>
                    Cartes de fidélité
                    {cardsTotal > 0 && <span className="scan-cards-count">{cardsTotal}</span>}
                  </h2>
                  <input
                    type="search"
                    className="scan-cards-search"
                    placeholder="Rechercher un client (nom, e-mail, n° de carte)..."
                    value={cardSearch}
                    onChange={(e) => { setCardSearch(e.target.value); setCardsPage(1); }}
                    aria-label="Rechercher une carte"
                  />
                </div>

                {cards.length === 0 ? (
                  <p className="scan-cards-empty">
                    Aucune carte ne correspond à cette recherche.
                  </p>
                ) : (
                  <ul className="scan-cards-list">
                    {cards.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="scan-cards-row"
                          onClick={() => { stopScanner(); lookupCard(c.id); }}
                        >
                          <span className="scan-cards-who">
                            <span className="scan-cards-client">{c.surname} {c.name}</span>
                            <span className="scan-cards-sub">{c.id} · {c.email}</span>
                          </span>
                          <span className="scan-cards-meta">
                            <span className="scan-cards-points">{c.points}</span>
                            <span className="scan-cards-visit">{formatDate(c.last_visit_at)}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {cardsPages > 1 && (
                  <div className="scan-cards-pagination">
                    <button
                      disabled={cardsPage <= 1}
                      onClick={() => setCardsPage(cardsPage - 1)}
                    >
                      Précédent
                    </button>
                    <span>{cardsPage} / {cardsPages}</span>
                    <button
                      disabled={cardsPage >= cardsPages}
                      onClick={() => setCardsPage(cardsPage + 1)}
                    >
                      Suivant
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="scan-card-detail">
            <div className="scan-card-info">
              <h2>{card.surname} {card.name}</h2>
              <p className="scan-card-id">{card.id}</p>
              <p className="scan-card-org">{card.organization}</p>
              <p className="scan-card-email">{card.email}</p>
              {isStamps ? (
                <div className="scan-stamps-display">
                  <div className="scan-points-display">
                    <span className="scan-points-value">{stamps}</span>
                    <span className="scan-points-label">tampon{stamps > 1 ? "s" : ""}</span>
                  </div>
                  <div className="scan-points-display">
                    <span className="scan-points-value">{rewards}</span>
                    <span className="scan-points-label">récompense{rewards > 1 ? "s" : ""}</span>
                  </div>
                </div>
              ) : (
                <div className="scan-points-display">
                  <span className="scan-points-value">{card.points}</span>
                  <span className="scan-points-label">points</span>
                </div>
              )}
            </div>

            {/* Sans ce rendu, un échec de crédit était totalement invisible :
                le commerçant croyait avoir validé le passage du client. */}
            {error && <div className="scan-error">{error}</div>}
            {message && <div className="scan-message">{message}</div>}

            {isStamps ? (
              <div className="scan-points-actions">
                <button
                  className="scan-stamp-add"
                  onClick={() => handlePoints(1, "+1 tampon ajouté")}
                >
                  Ajouter un tampon
                </button>
                <div className="scan-stamp-secondary">
                  <button
                    className="scan-pts-remove"
                    onClick={() => handlePoints(-1, "1 tampon retiré")}
                    disabled={card.points === 0}
                  >
                    Retirer un tampon
                  </button>
                  <button
                    className="scan-pts-remove"
                    onClick={() => handlePoints(-10, "1 récompense utilisée")}
                    disabled={rewards === 0}
                  >
                    Utiliser une récompense
                  </button>
                </div>
              </div>
            ) : (
              <div className="scan-points-actions">
                <div className="scan-quick-btns">
                  <button className="scan-pts-btn scan-pts-add" onClick={() => handlePoints(1)}>+1</button>
                  <button className="scan-pts-btn scan-pts-add" onClick={() => handlePoints(5)}>+5</button>
                  <button className="scan-pts-btn scan-pts-add" onClick={() => handlePoints(10)}>+10</button>
                  <button className="scan-pts-btn scan-pts-remove" onClick={() => handlePoints(-1)}>-1</button>
                  <button className="scan-pts-btn scan-pts-remove" onClick={() => handlePoints(-5)}>-5</button>
                  <button className="scan-pts-btn scan-pts-remove" onClick={() => handlePoints(-10)}>-10</button>
                </div>

                <div className="scan-custom-pts">
                  <input
                    type="number"
                    placeholder="Points personnalisés"
                    value={pointsInput}
                    onChange={(e) => setPointsInput(e.target.value)}
                  />
                  <div className="scan-custom-btns">
                    <button className="scan-pts-add" onClick={() => handlePoints(parseInt(pointsInput) || 0)}>Ajouter</button>
                    <button className="scan-pts-remove" onClick={() => handlePoints(-(parseInt(pointsInput) || 0))}>Retirer</button>
                  </div>
                </div>
              </div>
            )}

            <button className="scan-btn-new" onClick={() => { setCard(null); setMessage(""); setError(""); setPointsInput(""); }}>
              Scanner une autre carte
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default ScanCard;
